/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-nested-ternary */
/* eslint-disable react-hooks/rules-of-hooks */

import {
  Form,
  FormField,
  FormControl,
  FormItem,
  Textarea,
  FormLabel,
  Button,
  Checkbox,
} from "@repo/ui";
import { useUser } from "hooks/use-user";
import { StatusContext } from "pages/tickets/constants";
import { useGetParentTicketList } from "pages/tickets/utils";
import { useContext, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  useLookupResolutionCodes,
  useLookupResolutionCodesViaCategory,
} from "services/lookups";
import { useUpdateMaintenanceStatus } from "services/maintenance";
import { useUpdatePanicAlertIncidentStatus } from "services/panic-alert";
import { useUpdateTicketStatus } from "services/tickets";
import { FormDropdown } from "ui/combo-box";
import FormInput from "ui/Input";
import { Label } from "ui/label";
import Loader from "ui/page-loader";
import { DateTimePicker } from "@progress/kendo-react-dateinputs";
import { getLocalISODateString } from "utils/date-utils";
import { PATHS } from "routes/paths";

/**
 * @typedef {Object} Ticket
 * @property {boolean} isServiceTicket
 */

function ResolutionNotes({ requestId, ticketView, ticket = null }) {
  const { status } = useContext(StatusContext);
  const navigate = useNavigate();
  const user = useUser();

  const isCSMonitoring =
    user.Roles.includes("CS Monitoring") ||
    ticket?.activityType === "Panic Alert Incident" ||
    ticket?.serviceType === "Panic Alert Incident";

  const isOnlyCSM = user.Roles.includes("CSM Agent");
  const isCSMAgent =
    user.Roles.includes("CSM Agent") &&
    !(
      ticket?.activityType === "Panic Alert Incident" ||
      ticket?.activityType === "Incident - Standard" ||
      ticket?.activityType === "Incident - Emergency"
    );

  const resolutionCodesHook =
    ticket?.activityType === "Panic Alert Incident" || ticketView
      ? useLookupResolutionCodesViaCategory()
      : useLookupResolutionCodes();

  const statusUpdateHook = ticketView
    ? useUpdateTicketStatus()
    : isCSMonitoring
      ? useUpdatePanicAlertIncidentStatus()
      : useUpdateMaintenanceStatus();

  const {
    mutate: getResolutionCodes,
    data: resolutionCodesList,
    isPending: gettingResolutionCode,
  } = resolutionCodesHook;

  const {
    mutate: updateStatus,
    data: updateStatusRes,
    isPending: updatingStatus,
  } = statusUpdateHook;

  useEffect(() => {
    if (ticket?.activityType === "Panic Alert Incident") {
      getResolutionCodes("Panic Alert");
    } else if (ticketView) {
      if (ticket && !ticket.isServiceTicket) {
        getResolutionCodes("CC Tickets");
      }
    } else {
      getResolutionCodes();
    }
  }, [ticketView, ticket, getResolutionCodes]);

  const getHomeURL = useGetParentTicketList(
    status,
    ticket ? !ticket.isServiceTicket : false
  );
  useEffect(() => {
    if (updateStatusRes) {
      isOnlyCSM ? navigate(PATHS.panicAlertCalls) : navigate(getHomeURL);
    }
  }, [updateStatusRes]);

  const isTGSIHelpdesk = ticket?.activityType === "TGSI Helpdesk";

  const form = useForm({
    defaultValues: {
      resolvedBy: "",
      resolved: "",
      resolutionCode: "",
      ITName: "",
      resolutionNotes: "",
      cause: "",
      addToComments: true,
      serviceStartDateTime: "",
      serviceEndDateTime: "",
    },
  });
  const validateServiceTimes = (start, end) => {
    if (!start || !end) return true;
    return new Date(start) < new Date(end);
  };

  const onSubmit = (data) => {
    const basePayload = {
      resolutionCode: data.resolutionCode,
      resolutionNotes: data.resolutionNotes,
      itName: data.ITName,
      addToComments: data.addToComments,
      cause: data.cause,
    };

    // Include only when TGSI Helpdesk
    if (isTGSIHelpdesk) {
      basePayload.serviceStartDateTime = getLocalISODateString(
        data.serviceStartDateTime
      );
      basePayload.serviceEndDateTime = getLocalISODateString(
        data.serviceEndDateTime
      );
    }

    const payload =
      ticketView && ticket
        ? {
            ...basePayload,
            ticketId: requestId,
            status: "Resolved",
          }
        : {
            ...basePayload,
            requestId,
          };

    updateStatus(payload);
  };

  const generalProps = {
    errors: form.formState.errors,
    control: form.control,
  };

  return (
    <Form {...form} className="space-y-8 custom-form-v2">
      {gettingResolutionCode || updatingStatus ? <Loader /> : null}

      <form>
        <div className="form-row-d-50">
          <FormInput
            {...generalProps}
            label="Resolved By"
            name="resolvedBy"
            disabled
          />
          <FormInput
            {...generalProps}
            label="Resolved"
            name="resolved"
            disabled
            hideIcons
          />

          <FormDropdown
            {...generalProps}
            isRequired
            label="Resolution Code"
            name="resolutionCode"
            data={resolutionCodesList?.data || []}
            id="description"
            text="description"
            disabled={isCSMAgent}
            placeholder="Select Resolution Code"
            rules={{ required: "Select Value" }}
          />

          {!ticketView && (
            <FormInput {...generalProps} label="IT Name" name="ITName" />
          )}

          <FormField
            control={form.control}
            name="addToComments"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Add Resolution Notes To Comments</FormLabel>
                <div>
                  <FormControl>
                    <Checkbox
                      id="addToComments"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={isCSMAgent}
                    />
                  </FormControl>
                </div>
              </FormItem>
            )}
          />
        </div>

        {isTGSIHelpdesk ? (
          <div className="form-row-d-50 mt-6">
            <FormField
              control={generalProps.control}
              name="serviceStartDateTime"
              rules={{
                validate: (value) => {
                  const end = form.getValues("serviceEndDateTime");
                  if (!value) return "Service Start Time is required";
                  if (!validateServiceTimes(value, end))
                    return "Service Start Time must be earlier than Service End Time";
                  return true;
                },
              }}
              render={({ field }) => (
                <FormItem>
                  <Label label="Service Start Time" isRequired />
                  <FormControl>
                    <div className="form-date-picker">
                      <DateTimePicker
                        variant="secondary"
                        className="px-3 py-2 border border-secondary rounded-md"
                        value={field.value}
                        placeholder="Select Start Time"
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          form.trigger("serviceEndDateTime");
                        }}
                        disabled={isCSMAgent}
                      />
                    </div>
                  </FormControl>
                  {generalProps.errors?.serviceStartDateTime ? (
                    <p className="text-destructive text-xs mt-1">
                      {generalProps.errors.serviceStartDateTime.message}
                    </p>
                  ) : null}
                </FormItem>
              )}
            />

            <FormField
              control={generalProps.control}
              name="serviceEndDateTime"
              rules={{
                validate: (value) => {
                  const start = form.getValues("serviceStartDateTime");
                  if (!value) return "Service End Time is required";
                  if (!validateServiceTimes(start, value))
                    return "Service End Time must be later than Service Start Time";
                  return true;
                },
              }}
              render={({ field }) => (
                <FormItem>
                  <Label label="Service End Time" isRequired />
                  <FormControl>
                    <div className="form-date-picker">
                      <DateTimePicker
                        variant="secondary"
                        className="px-3 py-2 border border-secondary rounded-md"
                        value={field.value}
                        placeholder="Select End Time"
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          form.trigger("serviceStartDateTime");
                        }}
                        disabled={isCSMAgent}
                      />
                    </div>
                  </FormControl>
                  {generalProps.errors?.serviceEndDateTime ? (
                    <p className="text-destructive text-xs mt-1">
                      {generalProps.errors.serviceEndDateTime.message}
                    </p>
                  ) : null}
                </FormItem>
              )}
            />
          </div>
        ) : null}

        <FormTextAreaField
          isRequired
          control={form.control}
          name="resolutionNotes"
          label="Resolution Notes"
          errors={form.formState.errors}
          rules={{ required: "Resolution Notes are required" }}
          disabled={isCSMAgent}
        />

        <FormTextAreaField
          control={form.control}
          name="cause"
          label="Cause"
          errors={form.formState.errors}
          disabled={isCSMAgent}
        />

        <div className="flex flex-row w-full justify-end mt-6">
          <Button
            variant="secondary"
            onClick={form.handleSubmit(onSubmit)}
            disabled={isCSMAgent}
          >
            Update Resolution Notes
          </Button>
        </div>
      </form>
    </Form>
  );
}

function FormTextAreaField({
  control,
  name,
  label,
  errors,
  rules,
  isRequired,
}) {
  return (
    <div className="flex flex-col gap-inherit w-full mt-6">
      <FormField
        control={control}
        name={name}
        rules={rules}
        render={({ field }) => (
          <FormItem>
            <div className="flex flex-col gap-6 w-full">
              <Label label={label} isRequired={isRequired} />
              <div>
                <FormControl>
                  <Textarea
                    {...field}
                    className="min-w-full"
                    style={errors?.[name] ? { borderColor: "red" } : {}}
                  />
                </FormControl>
                {errors?.[name] ? (
                  <p className="text-destructive text-xs">
                    {errors[name].message}
                  </p>
                ) : null}
              </div>
            </div>
          </FormItem>
        )}
      />
    </div>
  );
}

ResolutionNotes.defaultProps = {
  ticket: null,
};

export default ResolutionNotes;
